import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { ResponseApi } from '../../types/Response/ResponseApi';
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Pagination,Tooltip } from '@mui/material';
import { Iconify } from '../../common/iconify';
import TematicaService from '../../services/TematicaService';
import SweetAlertUtil from '../../utils/SweetAlert';
import { Tematica, GetVoidTematica } from '../../types/Tematica/Tematica';
import { TematicaForm } from './TematicaForm';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface Props {
  data: ResponseApi<Tematica>,
  onUpdate?: (page: number, pageSize: number) => void,
}

export const TematicaTable: React.FC<Props> = ({ data, onUpdate }) => {
  const [rows, setRows] = React.useState<Array<Tematica>>([]);
  const [page, setPage] = React.useState(data.page ?? 1);
  const [totalPage, setTotalPage] = React.useState(data.totalPages ?? 1);
  const [pageSize, setPageSize] = React.useState(10);
  const [open, setOpen] = React.useState(false);
  const [tematica, setTematica] = React.useState(GetVoidTematica());

  async function ActivarTipo(id: number) {
    SweetAlertUtil.showLoading('Espere...');
    try {
      const response = await TematicaService.ActivarTematica(id);
      const data = response;
      SweetAlertUtil.hideLoading();
      if (data.status.isSuccessfully) {
        SweetAlertUtil.alertaExito('Ok', 'Temática activada.');
        if (onUpdate) {
          onUpdate(page, pageSize)
        }
      } else {
        SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problema.');
      }
    } catch (error) {
      SweetAlertUtil.hideLoading();
      console.error(error)
      SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problema.');
    }
  }

  async function InactivarTipo(id: number) {
    SweetAlertUtil.showLoading('Espere...');
    try {
      const response = await TematicaService.InactivarTematica(id);
      const data = response;
      SweetAlertUtil.hideLoading();
      if (data.status.isSuccessfully) {
        SweetAlertUtil.alertaExito('Ok', 'Temática inactivada.');
        if (onUpdate) {
          onUpdate(page, pageSize)
        }
      } else {
        SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problema.');
      }
    } catch (error) {
      SweetAlertUtil.hideLoading();
      console.error(error)
      SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problema.');
    }
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (onUpdate) {
      onUpdate(value, pageSize)
    }
    setPage(value);
  };

  function SelectTipo(obj: Tematica) {
    setTematica(obj);
    handleClickOpen()
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleResponse = (resp: any) => {
    handleClose()
    if (onUpdate) {
      onUpdate(page, pageSize)
    }
  };

  React.useEffect(() => {
    if (data?.listData) {
      setRows(data.listData);
    }
    if (data?.totalPages) {
      setTotalPage(data.totalPages);
    }
  }, [data]);

  return (
    <Paper sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
      gap: 2 // Espaciado entre elementos
    }}>
      {totalPage > 1 ? <Pagination count={totalPage} page={page} onChange={handlePageChange} showFirstButton showLastButton /> : <></>}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Nombre</StyledTableCell>
              <StyledTableCell>Descripción</StyledTableCell>
              <StyledTableCell>Estado</StyledTableCell>
              <StyledTableCell>Opciones</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.idTematica}>
                <StyledTableCell component="th" scope="row">
                  {row.txtNombre}
                </StyledTableCell>
                <StyledTableCell>{row.txtDescripcion}</StyledTableCell>
                <StyledTableCell>{row.estado === "A" ? "Activo" : "Inactivo"}</StyledTableCell>
                <StyledTableCell>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {row.estado === 'A' ?
                        <Tooltip title={'Inactivar'} placement='top' arrow>
                          <IconButton sx={{ color: '#61AFFE' }} onClick={() => { InactivarTipo(row.idTematica) }}>
                            <Iconify icon={'system-uicons:eye-no'} />
                          </IconButton>
                        </Tooltip>
                        :
                        <Tooltip title={'Activar'} placement='top' arrow>
                          <IconButton sx={{ color: '#61AFFE' }} onClick={() => { ActivarTipo(row.idTematica) }} >
                            <Iconify icon={'mdi-light:eye'} />
                          </IconButton>
                        </Tooltip>
                      }
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Tooltip title={'Editar'} placement='top' arrow>
                        <IconButton sx={{ color: '#61AFFE' }} onClick={() => { SelectTipo(row) }} >
                          <Iconify icon={'material-symbols:edit'} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPage > 1 ? <Pagination count={totalPage} page={page} onChange={handlePageChange} showFirstButton showLastButton /> : <></>}
      <Dialog
        open={open}
        onClose={handleClose}
      >
        <DialogTitle id="alert-dialog-title">
          {"Editar Tipo"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <TematicaForm tematica={tematica} onResponse={handleResponse} />
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}